/**
 * AssociationsQualityDashboard - Dashboard de calidad y operación de asociaciones
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Network,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Unlink,
  RefreshCw,
  HelpCircle,
  TreePine,
  Bus,
  Lightbulb,
  Trash2,
  Filter,
  Play,
  type LucideIcon,
  MapPin,
  Wrench,
  ShieldCheck,
  Eye,
  Building2,
} from 'lucide-react';
import { useAssociationStats } from '../hooks/useAssociationStats';
import {
  pointAssociationApi,
  type AssociationReviewItem,
  type AssociationStatus,
  type PointAssociationRun,
} from '../services/point-association-api.service';
import { POINT_ENTITY_REGISTRY } from '../config/point-entities.config';
import './associations-quality-dashboard.css';

interface AssociationsQualityDashboardProps {
  projectId: string;
  onFilterByStatus?: (status: AssociationStatus | null) => void;
  onShowUnassociated?: () => void;
}

const ENTITY_ICONS: Record<string, LucideIcon> = {
  tree: TreePine,
  transit_stop: Bus,
  streetlight: Lightbulb,
  waste_container: Trash2,
  building_centroid: Building2,
};

const ENTITY_LABELS: Record<string, string> = Object.fromEntries(
  POINT_ENTITY_REGISTRY.map((config) => [config.entityType, config.labelPlural]),
);

const ASSOCIATION_TYPE_LABELS: Record<string, string> = {
  edge: 'A segmento',
  node: 'A nodo',
  none: 'Sin asociar',
};

const ENTITY_EVENT_CONFIG: Record<string, { event: string; detailKey: string }> = {
  tree: { event: 'tree-select', detailKey: 'treeId' },
  transit_stop: { event: 'mobility-stops-select', detailKey: 'stopId' },
  waste_container: { event: 'waste-container-select', detailKey: 'containerId' },
  waste_depot: { event: 'waste-depot-select', detailKey: 'depotId' },
  waste_hotspot: { event: 'waste-hotspot-select', detailKey: 'hotspotId' },
};

const BATCH_ELIGIBLE_ENTITY_TYPES = POINT_ENTITY_REGISTRY.filter(
  (config) => config.allowsAutoRecalculation || config.entityType === 'building_centroid',
);

interface StatCardProps {
  label: string;
  value: number;
  total: number;
  color: 'green' | 'amber' | 'red' | 'gray' | 'blue' | 'slate';
  icon: LucideIcon;
  action?: {
    label: string;
    onClick: () => void;
  };
}

function StatCard({ label, value, total, color, icon: Icon, action }: StatCardProps) {
  const percentage = total > 0 ? Math.round((value / total) * 100) : 0;

  return (
    <div className={`stat-card stat-card-${color}`}>
      <div className="stat-card-header">
        <Icon size={18} className="stat-card-icon" />
        <span className="stat-card-label">{label}</span>
      </div>
      <div className="stat-card-value">{value}</div>
      <div className="stat-card-percentage">
        <div className="stat-card-bar" style={{ width: `${percentage}%` }} />
        <span>{percentage}%</span>
      </div>
      {action && value > 0 && (
        <button className="stat-card-action" onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  );
}

export function AssociationsQualityDashboard({
  projectId,
  onFilterByStatus,
  onShowUnassociated,
}: AssociationsQualityDashboardProps): JSX.Element {
  const { stats, isLoading, error, refetch, totals } = useAssociationStats(projectId);
  const [isRunningAction, setIsRunningAction] = useState(false);
  const [actionResult, setActionResult] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<AssociationStatus | null>(null);
  const [reviewItems, setReviewItems] = useState<AssociationReviewItem[]>([]);
  const [reviewModule, setReviewModule] = useState<string>('all');
  const [reviewEntityType, setReviewEntityType] = useState<string>('all');
  const [reviewState, setReviewState] = useState<'open' | 'resolved' | 'all'>('open');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [runs, setRuns] = useState<PointAssociationRun[]>([]);
  const [selectedBatchType, setSelectedBatchType] = useState<string>('tree');

  const availableModules = useMemo(
    () => Array.from(new Set(POINT_ENTITY_REGISTRY.map((config) => config.module))),
    [],
  );

  const availableEntityTypes = useMemo(() => {
    if (reviewModule === 'all') {
      return POINT_ENTITY_REGISTRY;
    }
    return POINT_ENTITY_REGISTRY.filter((config) => config.module === reviewModule);
  }, [reviewModule]);

  const loadOperationalData = useCallback(async () => {
    if (!projectId) {
      return;
    }

    setReviewLoading(true);
    try {
      const [nextReviews, nextRuns] = await Promise.all([
        pointAssociationApi.listReviewItems(projectId, {
          module: reviewModule === 'all' ? undefined : reviewModule,
          entityType: reviewEntityType === 'all' ? undefined : reviewEntityType,
          state: reviewState,
          limit: 60,
        }),
        pointAssociationApi.listRuns(projectId, 12),
      ]);

      setReviewItems(nextReviews);
      setRuns(nextRuns);
    } catch (fetchError) {
      console.error('[AssociationsQualityDashboard] Error loading operational data:', fetchError);
      setActionResult('Error al cargar pendientes operativos');
    } finally {
      setReviewLoading(false);
    }
  }, [projectId, reviewEntityType, reviewModule, reviewState]);

  useEffect(() => {
    void loadOperationalData();
  }, [loadOperationalData]);

  const refreshAll = useCallback(async () => {
    await Promise.all([refetch(), loadOperationalData()]);
  }, [loadOperationalData, refetch]);

  const handleRecalculateWeak = useCallback(async () => {
    setIsRunningAction(true);
    setActionResult(null);
    try {
      const run = await pointAssociationApi.enqueueRecalculatePending(projectId);
      setActionResult(`Job encolado para recalcular pendientes. Run ${run.id.slice(0, 8)}.`);
      await loadOperationalData();
      await refetch();
    } catch (err) {
      console.error('[Dashboard] Error recalculating:', err);
      setActionResult('Error al encolar recalculo de pendientes');
    } finally {
      setIsRunningAction(false);
    }
  }, [loadOperationalData, projectId, refetch]);

  const handleBatchAssociate = useCallback(async () => {
    setIsRunningAction(true);
    setActionResult(null);
    try {
      const run = await pointAssociationApi.enqueueAssociateBatch(
        projectId,
        selectedBatchType,
        true,
      );
      setActionResult(
        `Job encolado para asociar ${ENTITY_LABELS[selectedBatchType] || selectedBatchType}. Run ${run.id.slice(0, 8)}.`,
      );
      await loadOperationalData();
    } catch (err) {
      console.error('[Dashboard] Error enqueuing batch association:', err);
      setActionResult('Error al encolar asociación masiva');
    } finally {
      setIsRunningAction(false);
    }
  }, [loadOperationalData, projectId, selectedBatchType]);

  const handleAcceptSuggestion = useCallback(async (associationId: string) => {
    setIsRunningAction(true);
    setActionResult(null);
    try {
      await pointAssociationApi.acceptSuggestedRepair(projectId, associationId);
      setActionResult('Repair sugerido aceptado.');
      await refreshAll();
    } catch (err) {
      console.error('[Dashboard] Error accepting suggestion:', err);
      setActionResult('No se pudo aceptar la sugerencia de repair');
    } finally {
      setIsRunningAction(false);
    }
  }, [projectId, refreshAll]);

  const handleResolveReview = useCallback(
    async (
      associationId: string,
      action: 'kept_current' | 'resolved_manually' | 'discarded_suggestion',
    ) => {
      setIsRunningAction(true);
      setActionResult(null);
      try {
        await pointAssociationApi.resolveReview(projectId, associationId, { action });
        setActionResult('Pendiente marcado como resuelto.');
        await refreshAll();
      } catch (err) {
        console.error('[Dashboard] Error resolving review:', err);
        setActionResult('No se pudo cerrar el pendiente');
      } finally {
        setIsRunningAction(false);
      }
    },
    [projectId, refreshAll],
  );

  const handleFilterByStatus = useCallback(
    (status: AssociationStatus | null) => {
      setActiveFilter(status);
      onFilterByStatus?.(status);
    },
    [onFilterByStatus],
  );

  const navigateToEntity = useCallback((item: AssociationReviewItem) => {
    const config = ENTITY_EVENT_CONFIG[item.entityType];
    if (!config) {
      setActionResult(`No hay navegación directa configurada para ${item.entityType}.`);
      return;
    }

    window.dispatchEvent(
      new CustomEvent(config.event, {
        detail: {
          [config.detailKey]: item.entityId,
          elementId: item.entityId,
        },
      }),
    );
  }, []);

  const navigateToTarget = useCallback((item: AssociationReviewItem) => {
    const targetId = item.suggestedTarget?.id ?? item.currentTarget.id;
    const targetType = item.suggestedTarget?.type ?? item.currentTarget.type;

    if (!targetId || targetType === 'none') {
      setActionResult('No hay segmento o nodo disponible para navegar.');
      return;
    }

    window.dispatchEvent(
      new CustomEvent(targetType === 'edge' ? 'network-edges-select' : 'network-nodes-select', {
        detail: {
          [`${targetType}Id`]: targetId,
          elementId: targetId,
        },
      }),
    );
  }, []);

  if (isLoading && !stats) {
    return (
      <div className="associations-dashboard loading">
        <div className="dashboard-header">
          <Network size={20} />
          <h3>Estado de Asociaciones</h3>
        </div>
        <div className="loading-indicator">
          <RefreshCw size={24} className="spin" />
          <span>Cargando estadísticas...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="associations-dashboard error">
        <div className="dashboard-header">
          <Network size={20} />
          <h3>Estado de Asociaciones</h3>
        </div>
        <div className="error-message">
          <AlertCircle size={24} />
          <span>Error al cargar estadísticas</span>
          <button onClick={refreshAll}>Reintentar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="associations-dashboard">
      <div className="dashboard-header">
        <Network size={20} />
        <h3>Estado de Asociaciones</h3>
        <button
          className="refresh-btn"
          onClick={refreshAll}
          disabled={isLoading || reviewLoading}
          title="Actualizar estadísticas"
        >
          <RefreshCw size={16} className={isLoading || reviewLoading ? 'spin' : ''} />
        </button>
      </div>

      <div className="stats-grid">
        <StatCard label="Total" value={totals.total} total={totals.total} color="slate" icon={Network} />
        <StatCard
          label="Válidas"
          value={totals.valid}
          total={totals.total}
          color="green"
          icon={CheckCircle2}
          action={onFilterByStatus ? { label: 'Ver', onClick: () => handleFilterByStatus('valid') } : undefined}
        />
        <StatCard
          label="Débiles"
          value={totals.weak}
          total={totals.total}
          color="amber"
          icon={AlertTriangle}
          action={onFilterByStatus ? { label: 'Revisar', onClick: () => handleFilterByStatus('weak') } : undefined}
        />
        <StatCard
          label="Revisión"
          value={totals.needsReview}
          total={totals.total}
          color="red"
          icon={AlertCircle}
          action={onFilterByStatus ? { label: 'Corregir', onClick: () => handleFilterByStatus('needs_review') } : undefined}
        />
        <StatCard
          label="Huérfanas"
          value={totals.orphaned}
          total={totals.total}
          color="gray"
          icon={Unlink}
          action={onFilterByStatus ? { label: 'Revisar', onClick: () => handleFilterByStatus('orphaned') } : undefined}
        />
        <StatCard
          label="Pendientes"
          value={totals.needsRecalculation}
          total={totals.total}
          color="blue"
          icon={RefreshCw}
          action={totals.needsRecalculation > 0 ? { label: 'Encolar', onClick: handleRecalculateWeak } : undefined}
        />
      </div>

      {stats?.byEntityType && Object.keys(stats.byEntityType).length > 0 && (
        <div className="breakdown-section">
          <h4>Por tipo de entidad</h4>
          <div className="breakdown-list">
            {Object.entries(stats.byEntityType).map(([type, count]) => {
              const Icon = ENTITY_ICONS[type] || HelpCircle;
              const label = ENTITY_LABELS[type] || type;
              return (
                <div key={type} className="breakdown-item">
                  <Icon size={14} />
                  <span className="breakdown-label">{label}</span>
                  <span className="breakdown-count">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {stats?.byAssociationType && Object.keys(stats.byAssociationType).length > 0 && (
        <div className="breakdown-section">
          <h4>Por tipo de conexión</h4>
          <div className="breakdown-list">
            {Object.entries(stats.byAssociationType).map(([type, count]) => (
              <div key={type} className="breakdown-item">
                <span className="breakdown-label">{ASSOCIATION_TYPE_LABELS[type] || type}</span>
                <span className="breakdown-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeFilter && (
        <div className="active-filter">
          <Filter size={14} />
          <span>Filtrando: {activeFilter}</span>
          <button onClick={() => handleFilterByStatus(null)}>Limpiar filtro</button>
        </div>
      )}

      <div className="dashboard-actions">
        <button
          className="action-btn action-btn-primary"
          onClick={handleRecalculateWeak}
          disabled={isRunningAction || totals.needsRecalculation === 0}
        >
          {isRunningAction ? (
            <>
              <RefreshCw size={14} className="spin" />
              Ejecutando...
            </>
          ) : (
            <>
              <Play size={14} />
              Encolar recálculo
            </>
          )}
        </button>
        <select
          className="dashboard-select"
          value={selectedBatchType}
          onChange={(event) => setSelectedBatchType(event.target.value)}
        >
          {BATCH_ELIGIBLE_ENTITY_TYPES.map((config) => (
            <option key={config.entityType} value={config.entityType}>
              {config.labelPlural}
            </option>
          ))}
        </select>
        <button className="action-btn" onClick={handleBatchAssociate} disabled={isRunningAction}>
          <Wrench size={14} />
          Encolar asociación masiva
        </button>
        {onShowUnassociated && (
          <button className="action-btn" onClick={onShowUnassociated}>
            <HelpCircle size={14} />
            Ver sin asociar
          </button>
        )}
      </div>

      <div className="breakdown-section">
        <h4>Jobs recientes</h4>
        {runs.length === 0 ? (
          <div className="review-empty">No hay jobs recientes.</div>
        ) : (
          <div className="review-list">
            {runs.map((run) => (
              <div key={run.id} className="review-item compact">
                <div className="review-main">
                  <div className="review-title">
                    {run.operationType === 'associate_batch' ? 'Asociación masiva' : 'Recalcular pendientes'}
                  </div>
                  <div className="review-subtitle">
                    {run.entityType ? `${ENTITY_LABELS[run.entityType] || run.entityType} · ` : ''}
                    {run.status} · {run.progressPct}%
                  </div>
                </div>
                <div className={`review-state-badge ${run.status}`}>{run.status}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="breakdown-section">
        <div className="review-header">
          <h4>Pendientes operativos</h4>
          {reviewLoading && <RefreshCw size={14} className="spin" />}
        </div>
        <div className="review-filters">
          <select className="dashboard-select" value={reviewModule} onChange={(e) => {
            setReviewModule(e.target.value);
            setReviewEntityType('all');
          }}>
            <option value="all">Todos los módulos</option>
            {availableModules.map((module) => (
              <option key={module} value={module}>{module}</option>
            ))}
          </select>
          <select className="dashboard-select" value={reviewEntityType} onChange={(e) => setReviewEntityType(e.target.value)}>
            <option value="all">Todos los tipos</option>
            {availableEntityTypes.map((config) => (
              <option key={config.entityType} value={config.entityType}>{config.labelPlural}</option>
            ))}
          </select>
          <select className="dashboard-select" value={reviewState} onChange={(e) => setReviewState(e.target.value as 'open' | 'resolved' | 'all')}>
            <option value="open">Abiertos</option>
            <option value="resolved">Resueltos</option>
            <option value="all">Todos</option>
          </select>
        </div>

        {reviewItems.length === 0 ? (
          <div className="review-empty">No hay asociaciones para este filtro.</div>
        ) : (
          <div className="review-list">
            {reviewItems.map((item) => (
              <div key={item.associationId} className="review-item">
                <div className="review-main">
                  <div className="review-title">
                    {item.entityLabel} · {item.entityId.slice(0, 8)}
                  </div>
                  <div className="review-subtitle">
                    Actual: {item.currentTarget.type}:{item.currentTarget.id?.slice(0, 8) ?? 'sin target'}
                    {item.suggestedTarget ? ` · Sugerido: ${item.suggestedTarget.type}:${item.suggestedTarget.id.slice(0, 8)}` : ''}
                  </div>
                  <div className="review-meta">
                    <span>{item.reviewReason ?? 'Sin motivo explícito'}</span>
                    {item.distanceToNetwork !== null && <span>· {item.distanceToNetwork.toFixed(1)} m</span>}
                    {item.reviewSource && <span>· {item.reviewSource}</span>}
                  </div>
                </div>
                <div className="review-side">
                  <div className={`review-state-badge ${item.reviewState}`}>{item.reviewState}</div>
                  <div className="review-actions">
                    {item.reviewState === 'auto_repairable' && (
                      <button className="mini-btn" onClick={() => handleAcceptSuggestion(item.associationId)}>
                        <ShieldCheck size={12} />
                        Aceptar
                      </button>
                    )}
                    <button className="mini-btn" onClick={() => navigateToEntity(item)}>
                      <Eye size={12} />
                      Entidad
                    </button>
                    <button className="mini-btn" onClick={() => navigateToTarget(item)}>
                      <MapPin size={12} />
                      Segmento
                    </button>
                    {item.reviewState !== 'resolved' && (
                      <>
                        <button className="mini-btn" onClick={() => handleResolveReview(item.associationId, 'kept_current')}>
                          Mantener
                        </button>
                        <button className="mini-btn" onClick={() => handleResolveReview(item.associationId, 'discarded_suggestion')}>
                          Descartar
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {actionResult && (
        <div className={`recalculate-result ${actionResult.includes('Error') ? 'error' : 'success'}`}>
          {actionResult}
        </div>
      )}
    </div>
  );
}

export default AssociationsQualityDashboard;
